import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  DocumentArrowDownIcon ,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BuildCoinsSettings = () => {
  const [settings, setSettings] = useState({
    conversion_rate: 1,
    minimum_redemption: 100,
    maximum_redemption: 10000,
    coin_expiry_days: 365,
    auto_approval_limit: 1000,
    notification_settings: {
      low_balance_threshold: 100,
      expiry_warning_days: 30,
      email_notifications: true,
      sms_notifications: false
    },
    usage_categories: [
      { name: 'purchase_discount', enabled: true, max_percentage: 50 },
      { name: 'shipping_discount', enabled: true, max_percentage: 100 },
      { name: 'product_unlock', enabled: true, max_percentage: 100 },
      { name: 'premium_feature', enabled: false, max_percentage: 100 }
    ]
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/buildcoins/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/buildcoins/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const updateUsageCategory = (index, field, value) => {
    const updatedCategories = [...settings.usage_categories];
    updatedCategories[index] = { ...updatedCategories[index], [field]: value };
    setSettings({ ...settings, usage_categories: updatedCategories });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">BuildCoins Settings</h1>
          <p className="text-gray-600">Configure BuildCoins system parameters</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <DocumentArrowDownIcon  className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">General Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conversion Rate (₹ per coin)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settings.conversion_rate}
                onChange={(e) => setSettings({ ...settings, conversion_rate: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Redemption (coins)
              </label>
              <input
                type="number"
                min="1"
                value={settings.minimum_redemption}
                onChange={(e) => setSettings({ ...settings, minimum_redemption: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Redemption (coins)
              </label>
              <input
                type="number"
                min="1"
                value={settings.maximum_redemption}
                onChange={(e) => setSettings({ ...settings, maximum_redemption: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coin Expiry (days)
              </label>
              <input
                type="number"
                min="1"
                value={settings.coin_expiry_days}
                onChange={(e) => setSettings({ ...settings, coin_expiry_days: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto Approval Limit (coins)
              </label>
              <input
                type="number"
                min="0"
                value={settings.auto_approval_limit}
                onChange={(e) => setSettings({ ...settings, auto_approval_limit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Transactions below this limit will be auto-approved
              </p>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Low Balance Threshold (coins)
              </label>
              <input
                type="number"
                min="0"
                value={settings.notification_settings.low_balance_threshold}
                onChange={(e) => setSettings({
                  ...settings,
                  notification_settings: {
                    ...settings.notification_settings,
                    low_balance_threshold: parseInt(e.target.value)
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Warning (days before)
              </label>
              <input
                                type="number"
                min="1"
                value={settings.notification_settings.expiry_warning_days}
                onChange={(e) => setSettings({
                  ...settings,
                  notification_settings: {
                    ...settings.notification_settings,
                    expiry_warning_days: parseInt(e.target.value)
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="email_notifications"
                checked={settings.notification_settings.email_notifications}
                onChange={(e) => setSettings({
                  ...settings,
                  notification_settings: {
                    ...settings.notification_settings,
                    email_notifications: e.target.checked
                  }
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="email_notifications" className="ml-2 block text-sm text-gray-900">
                Enable Email Notifications
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="sms_notifications"
                checked={settings.notification_settings.sms_notifications}
                onChange={(e) => setSettings({
                  ...settings,
                  notification_settings: {
                    ...settings.notification_settings,
                    sms_notifications: e.target.checked
                  }
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="sms_notifications" className="ml-2 block text-sm text-gray-900">
                Enable SMS Notifications
              </label>
            </div>
          </div>
        </div>

        {/* Usage Categories */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Usage Categories</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Discount %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {settings.usage_categories.map((category, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 capitalize">
                        {category.name.replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={category.enabled}
                          onChange={(e) => updateUsageCategory(index, 'enabled', e.target.checked)}
                          className="form-checkbox h-4 w-4 text-blue-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {category.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={category.max_percentage}
                        onChange={(e) => updateUsageCategory(index, 'max_percentage', parseInt(e.target.value))}
                        disabled={!category.enabled}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                      <span className="ml-1 text-sm text-gray-500">%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => updateUsageCategory(index, 'enabled', !category.enabled)}
                        className={`${
                          category.enabled 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                      >
                        {category.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Warning Section */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Important Notes
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Changes to conversion rates will affect all future transactions</li>
                <li>Modifying expiry settings will only apply to newly credited coins</li>
                <li>Disabling usage categories will prevent customers from using coins for those purposes</li>
                <li>Auto-approval limits help reduce manual review workload</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildCoinsSettings;
